import React, { useRef } from 'react';
import {
  TableRow,
  TableCell,
  Avatar,
  Typography,
  Chip,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { getFileIcon } from '../../../../utils/getFileIcon';
import { getFileTypeColor, getFileIconColor } from '../../../../utils/getFileTypeColor';
import { formatFileSize } from '../../../../utils/formatFileSize';
import { getFileTypeLabel } from '../../../../utils/getFileTypeLabel';
import { FileActionsMenu } from '../../modules/FileActionsMenu';
import { InlineRenameField, hasAction, useInlineRename } from '../../modules/InlineRename';
import { AccessMark, SharedFileMark } from '../../modules/FolderGlyph';
import { isOwnItem } from '../../../../utils/getFolderName';
import StaffMember from '../../../../../../components/common/StaffMember';
import StaffMemberById from '../../../../../../components/common/StaffMemberById';
import { tokenService } from '../../../../../auth/services/tokenService';

const UPLOADER_PROPS = { avatarSize: 24, imageSize: '32x32', gap: 1, nameVariant: 'body2', nameSx: { fontWeight: 400 } };

export const FileRow = ({
  file,
  selected,
  secondarySelected,
  disabled = false,
  onClick,
  dragData,
  actions,
  onFileAction,
  // The actions column; also there when only folders have actions
  hasActions = actions?.length > 0,
  checkable = false,
  // In a checkable list, whether this file can be ticked
  canCheck = true,
  checked = false,
  onToggleCheck,
  // (file, newName) => Promise<boolean>; with a "rename" action, the name can be edited in place
  onRename,
}) => {
  const { t } = useTranslation(['files']);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable(
    dragData ?? { id: `drag-disabled-${file.file_id}`, disabled: true }
  );
  const rowRef = useRef(null);
  const { renaming, stopRenaming, nameProps, rowKeyDown } = useInlineRename({
    enabled: Boolean(onRename) && hasAction(actions, 'rename') && !disabled,
  });

  // Another department's staff (on its shared files) can't be opened from here
  const uploaderOpens = tokenService.hasScope('staff:read')
    && Boolean(file.uploader ? isOwnItem(file) : file.uploader_id);

  // Selected (and, when comparing, second-selected) rows get a tint rather than a
  // solid fill, so the text stays readable
  const tint = selected ? 'info.main' : secondarySelected ? 'warning.main' : null;

  return (
    <TableRow
      ref={(el) => { setNodeRef(el); rowRef.current = el; }}
      hover
      selected={selected || secondarySelected}
      {...(dragData && !dragData.disabled && !disabled ? { ...listeners, ...attributes } : {})}
      // Rows open with Enter/Space like a button; dnd-kit's attributes would set role="button" too
      tabIndex={disabled ? -1 : 0}
      aria-selected={selected || secondarySelected}
      aria-disabled={disabled || undefined}
      sx={{
        cursor: isDragging ? 'grabbing' : (disabled ? 'not-allowed' : (dragData && !dragData.disabled ? 'grab' : 'pointer')),
        opacity: isDragging ? 0.4 : (disabled ? 0.5 : 1),
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        ...(tint && {
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: (theme) => alpha(theme.palette[tint.split('.')[0]].main, 0.16),
          },
          '& td:first-of-type': { boxShadow: (theme) => `inset 3px 0 ${theme.palette[tint.split('.')[0]].main}` },
        }),
      }}
      // The second click of a double-click (renaming) mustn't select again
      onClick={(e) => { if (!disabled && e.detail <= 1) onClick(file, e); }}
      onKeyDown={(e) => {
        if (disabled || e.target !== e.currentTarget || rowKeyDown(e)) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(file, e);
        }
      }}
    >
      {checkable && (
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          {canCheck && (
            <Checkbox
              size="small"
              checked={checked}
              disabled={disabled}
              onPointerDown={(e) => e.stopPropagation()}
              onChange={() => onToggleCheck?.(file)}
              inputProps={{ 'aria-label': t('files:select_named', { name: file.filename }) }}
            />
          )}
        </TableCell>
      )}
      <TableCell>
        <Avatar
          sx={{
            bgcolor: 'transparent',
            color: getFileIconColor(file.filetype),
            width: 30,
            height: 30,
          }}
        >
          {getFileIcon(file.filetype)}
        </Avatar>
      </TableCell>
      <TableCell>
        {renaming ? (
          <InlineRenameField
            name={file.filename}
            keepExtension
            onSave={(newName) => onRename(file, newName)}
            onClose={({ keyboard }) => {
              stopRenaming();
              if (keyboard) rowRef.current?.focus();
            }}
            sx={{ maxWidth: 360, '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
          />
        ) : (
          <>
            <Typography
              variant="body2"
              noWrap
              title={file.filename}
              {...nameProps}
              sx={{ display: 'inline-block', maxWidth: 300, verticalAlign: 'middle' }}
            >
              {file.filename}
            </Typography>
            <SharedFileMark file={file} sx={{ ml: 0.75, verticalAlign: 'middle' }} />
            <AccessMark item={file} sx={{ ml: 0.75, verticalAlign: 'middle' }} />
          </>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={getFileTypeLabel(file)}
          size="small"
          color={getFileTypeColor(file.filetype)}
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatFileSize(file.file_size)}
        </Typography>
      </TableCell>
      {/* The person opens their page; a click on them mustn't also select the file */}
      <TableCell onClick={(e) => { if (uploaderOpens && e.target !== e.currentTarget) e.stopPropagation(); }}>
        {file.uploader ? (
          <StaffMember {...UPLOADER_PROPS} staffMember={file.uploader} clickable={uploaderOpens} />
        ) : file.uploader_id ? (
          <StaffMemberById {...UPLOADER_PROPS} staffId={file.uploader_id} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {file.uploader_name || '—'}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {new Date(file.uploaded_at).toLocaleDateString()}
        </Typography>
      </TableCell>
      {hasActions && (
        <TableCell align="right" padding="none" sx={{ pr: 1 }}>
          {!disabled && <FileActionsMenu file={file} actions={actions} onAction={onFileAction} />}
        </TableCell>
      )}
    </TableRow>
  );
};
